import type { Language } from '@eformulieren/engine'
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef } from 'react'
import { BusyButton } from './BusyButton.js'

/**
 * De gedeelde modale dialoog van de renderer (R-139, R-140, R-169, WCAG 2.4.3).
 *
 * Eén patroon voor een vraag die het formulier even onderbreekt: een opmerking plaatsen, en straks de
 * andere invoer die nu nog onder een veld openklapt. Het kader is dat van de dialoog voor delen en
 * advies vragen (`ShareDialog`, dezelfde klassen in `schil.css`): een vaste kop met de titel, een inhoud
 * die scrolt en een vaste voet met de knoppen; op een telefoon het hele scherm.
 *
 * **Modaal.** `<dialog>` met `showModal()`: de rest van de pagina is inert, Tab en Shift+Tab lopen rond
 * binnen de dialoog, en Escape sluit hem (`onSluit`). De focus gaat bij het openen naar het element in
 * de inhoud met `data-autofocus`, en anders naar het eerste dat focus kan krijgen.
 *
 * **De focus terug.** Na het sluiten naar `focusTerug` als de aanroeper die geeft (de tekst waarin een
 * opmerking werd begonnen), en anders naar wat de focus had vóór het openen.
 *
 * **Wie sluit.** De dialoog toont wat hij krijgt; de aanroeper haalt hem weg. De secundaire knop en
 * Escape roepen `onSluit`, tenzij de secundaire knop een eigen handeling heeft. De primaire knop roept
 * alleen zijn handeling; sluiten na een geslaagde handeling is aan de aanroeper.
 */

export interface DialogAction {
  readonly label: string
  readonly onActie?: () => unknown
  /** Alleen bij de primaire knop: de tekst terwijl de handeling loopt. */
  readonly bezigTekst?: string
  readonly uitgeschakeld?: boolean
}

export interface DialogProps {
  readonly titel: string
  readonly taal: Language
  readonly children: ReactNode
  readonly primair?: DialogAction & { readonly bezigTekst: string }
  readonly secundair?: DialogAction
  /** Escape, of de secundaire knop zonder eigen handeling. */
  readonly onSluit: () => void
  /** Waar de focus heen gaat na het sluiten; zonder dit naar wat de focus had vóór het openen. */
  readonly focusTerug?: () => void
  /** Een extra klasse voor de inhoud, voor wat alleen deze dialoog nodig heeft. */
  readonly className?: string
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])'

export function Dialog({ titel: title, taal: language, children, primair: primary, secundair: secondary, onSluit: onClose, focusTerug: focusBack, className }: DialogProps): ReactNode {
  const modal = useRef<HTMLDialogElement>(null)
  const headerId = useId()
  const back = useRef(focusBack)
  back.current = focusBack

  useEffect(() => {
    const element = modal.current
    if (element === null) return
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null
    if (!element.open) element.showModal()
    const first = element.querySelector<HTMLElement>('[data-autofocus]') ?? element.querySelector<HTMLElement>(FOCUSABLE)
    first?.focus()
    return () => {
      if (element.open) element.close()
      if (back.current !== undefined) back.current()
      else previous?.focus()
    }
  }, [])

  function keepFocusWithin(event: KeyboardEvent<HTMLDialogElement>): void {
    if (event.key !== 'Tab' || modal.current === null) return
    const within = Array.from(modal.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((element) => element.offsetParent !== null)
    const first = within[0]
    const last = within.at(-1)
    if (first === undefined || last === undefined) return
    const active = document.activeElement
    if (event.shiftKey && (active === first || active === modal.current)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && active === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    <dialog
      ref={modal}
      className="ef-deeldialoog ef-dialoog"
      aria-labelledby={headerId}
      lang={language}
      onKeyDown={keepFocusWithin}
      onCancel={(event) => {
        // Escape: React houdt bij of de dialoog open is, dus de browser sluit hem niet zelf.
        event.preventDefault()
        onClose()
      }}
    >
      <div className="ef-deeldialoog__kop">
        <div className="ef-deeldialoog__kopregel">
          <h2 id={headerId} className="ef-deeldialoog__titel">
            {title}
          </h2>
        </div>
      </div>
      <div className={className === undefined ? 'ef-deeldialoog__inhoud' : `ef-deeldialoog__inhoud ${className}`}>{children}</div>
      {primary === undefined && secondary === undefined ? null : (
        <div className="ef-deeldialoog__voet ef-dialoog__voet">
          {secondary === undefined ? null : (
            <button
              type="button"
              className="ef-actieknop"
              data-soort="secundair"
              disabled={secondary.uitgeschakeld === true}
              onClick={() => (secondary.onActie === undefined ? onClose() : secondary.onActie())}
            >
              {secondary.label}
            </button>
          )}
          {primary === undefined ? null : (
            <BusyButton
              className="ef-actieknop"
              data-soort="primair"
              busyText={primary.bezigTekst}
              disabled={primary.uitgeschakeld === true}
              {...(primary.onActie === undefined ? {} : { onAction: primary.onActie })}
            >
              {primary.label}
            </BusyButton>
          )}
        </div>
      )}
    </dialog>
  )
}
